import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import AISystemIcon from '../assets/AI_systems_dashboard.svg';
import DocumentsIcon from '../assets/documents_dashboard.svg';
import PendingReqIcon from '../assets/pending_req_dashboard.svg';
import UsersIcon from '../assets/users_dashboard.svg';
import { AddAISystemBanner } from '../components/features/AddAISystemBanner';
import { DashboardCard } from '../components/features/DashboardCards/DashboardCard';
import { EUAIActTimeline } from '../components/features/EUAIActTimeline/EUAIActTimeline';
import { RecentActivity } from '../components/features/RecentActivity/RecentActivity';
import { Reference } from '../components/features/Reference/Reference';
import { RiskCategories } from '../components/features/RiskCategories/RiskCategories';
import { Subscription } from '../components/features/Subscription/Subscription';
import { TutorialGuideModal } from '../components/features/TutorialGuide/TutorialGuideModal';
import Loader from '../components/ui/Loader/Loader';
import { PATHS } from '../constants/Paths.constants';
import { AppLayout } from '../layout/AppLayout';
import { DashboardLayout } from '../layout/DashboardLayout';
import { fetchDashboard } from '../services/DashboardService';
import { AppDispatch, RootState } from '../store';
import {
  fetchDashboardFailure,
  fetchDashboardStart,
  fetchDashboardSuccess
} from '../store/slices/dashboardSlice';
import {
  openTutorialGuide,
  setCanShowTutorialBannerFromOnboarding,
  setShowTutorialBanner,
  setTutorialGuideBootstrapResolved,
  setTutorialGuideSessionId
} from '../store/slices/tutorialGuideSlice';
import { getLatestOnboardingSession } from '../services/OnboardingService';
import { startTutorialGuideFlow } from '../utils/Functions.utils';

export function Dashboard() {
  const dispatch = useDispatch<AppDispatch>();
  const { data, loading, error } = useSelector(
    (state: RootState) => state.dashboard
  );
  const {
    hasResolvedBootstrap,
    canShowTutorialBannerFromOnboarding,
    hasTutorialBannerExpired
  } = useSelector((state: RootState) => state.tutorialGuide);

  useEffect(() => {
    const loadDashboardData = async () => {
      dispatch(fetchDashboardStart());
      try {
        const dashboardData = await fetchDashboard();
        dispatch(fetchDashboardSuccess(dashboardData));
      } catch (err: any) {
        dispatch(fetchDashboardFailure(err.message));
      }
    };

    loadDashboardData();
  }, [dispatch]);

  useEffect(() => {
    if (!data || !hasResolvedBootstrap) return;

    dispatch(
      setShowTutorialBanner(
        canShowTutorialBannerFromOnboarding &&
          !hasTutorialBannerExpired &&
          !data.hasAiSystems
      )
    );
  }, [
    canShowTutorialBannerFromOnboarding,
    data,
    dispatch,
    hasResolvedBootstrap,
    hasTutorialBannerExpired
  ]);

  useEffect(() => {
    if (hasResolvedBootstrap) return;

    const startTutorialGuide = async () => {
      try {
        const response = await startTutorialGuideFlow(
          dispatch,
          'WELCOME_SCREEN'
        );
        dispatch(setTutorialGuideBootstrapResolved(true));
        return response;
      } catch (error) {
        console.error('Error starting onboarding guide:', error);
      }
    };

    const fetchTutorialProgress = async () => {
      try {
        const response = await getLatestOnboardingSession();
        // no onboarding session found, likely reload browser on first login - open tutorial guide
        if (response.status === 404) {
          await startTutorialGuide();
          return;
        }

        if (
          response.data?.status === 'ONBOARDING_INITIATED' ||
          response.data?.status === 'ONBOARDING_STEP_VIEW'
        ) {
          dispatch(openTutorialGuide(response.data.currentStep));
        }

        if (
          response.data?.isOnboardingBannerDismissed ||
          response.data?.hasCompletedOnboardingOnce ||
          hasTutorialBannerExpired
        ) {
          dispatch(setCanShowTutorialBannerFromOnboarding(false));
          dispatch(setShowTutorialBanner(false));
        } else {
          dispatch(setCanShowTutorialBannerFromOnboarding(true));
        }

        dispatch(setTutorialGuideSessionId(response.data?.id ?? null));
      } catch (error) {
        console.error('Error fetching onboarding session:', error);
      } finally {
        dispatch(setTutorialGuideBootstrapResolved(true));
      }
    };
    fetchTutorialProgress();
  }, [dispatch, hasResolvedBootstrap, hasTutorialBannerExpired]);

  if (loading || !hasResolvedBootstrap) {
    return (
      <AppLayout>
        <DashboardLayout
          title="Welcome to the Dashboard!"
          description="Loading your compliance overview..."
        >
          <div className="flex items-center justify-center h-64">
            <Loader />
          </div>
        </DashboardLayout>
      </AppLayout>
    );
  }

  const hasData = !error && data;
  const hasAiSystems = hasData && data.hasAiSystems;

  return (
    <AppLayout>
      <DashboardLayout
        title={
          hasData
            ? `Welcome to the Dashboard, ${data.firstName}!`
            : 'Welcome to the Dashboard!'
        }
        description={"Here's your compliance overview."}
      >
        <div className="flex flex-col gap-6 w-full">
          {!hasAiSystems && <AddAISystemBanner />}

          {hasAiSystems && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 w-full">
              <DashboardCard
                title="AI SYSTEMS"
                count={data.informationBlock.aiSystemsCount}
                icon={
                  <img
                    src={AISystemIcon}
                    alt="AI Systems"
                    className="w-12 h-12"
                  />
                }
              />
              <DashboardCard
                title="DOCUMENTS"
                count={data.informationBlock.totalDocuments}
                icon={
                  <img
                    src={DocumentsIcon}
                    alt="Documents"
                    className="w-12 h-12"
                  />
                }
              />
              <DashboardCard
                title="PENDING REQUIREMENTS"
                count={data.informationBlock.pendingRequirements}
                icon={
                  <img
                    src={PendingReqIcon}
                    alt="Pending Requirements"
                    className="w-12 h-12"
                  />
                }
              />
              <DashboardCard
                title="USERS"
                count={data.informationBlock.totalUsers}
                icon={<img src={UsersIcon} alt="Users" className="w-12 h-12" />}
              />
            </div>
          )}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 w-full items-start">
            <div className="flex flex-col gap-6">
              <EUAIActTimeline timeline={hasData ? data.timeline : []} />
              <Reference />
            </div>
            <div className="flex flex-col gap-6">
              <Subscription subscription={hasData ? data.subscription : null} />
              <RecentActivity activities={hasData ? data.recentActivity : []} />
            </div>
          </div>
          <RiskCategories />
        </div>
      </DashboardLayout>
      <TutorialGuideModal />
    </AppLayout>
  );
}
